import { useT, type Translate } from "../../../shared/i18n/i18n";
import { Combobox as ComboboxPrimitive } from "@base-ui/react/combobox";
import { Check, ChevronDown, Search, Star, X } from "lucide-react";
import { type ReactNode, useMemo, useRef, useState } from "react";
import type { WorkObjectFieldDefinition } from "../../work-object-field/model/use-work-object-fields";
import { formatDurationFromSeconds, parseDurationToSeconds } from "../../work-object-field/model/duration-field";
import { emptyValueLabel, isMultiValueField, isRelationField, type WorkObjectFieldValueDraft } from "../model/work-object-field-drafts";
import type { WorkObjectListView } from "../model/use-work-objects";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-field";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

// "cell" — compact ghost editors inside board table cells; "panel" — ghost editors in the
// task-detail panel grid; "form" — standalone forms (intake), where every control wears the
// design-kit input chrome (border + fill) instead of the ghost style.
export type FieldEditorSurface = "cell" | "panel" | "form";

// Chrome for button-like triggers (date picker, combobox) on the form surface, mirroring the
// design-kit Input look including its focus ring.
const formTriggerClass =
  "border border-input bg-input/20 outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 dark:bg-input/30";

// note — приписка справа от варианта: сейчас это «ещё не заходил» у тех,
// кого позвали, но кто пока не открывал продукт.
type ComboOption = { color?: string; icon?: ReactNode; id: string; label: string; note?: string };

const NUMERIC_TYPES = new Set<WorkObjectFieldDefinition["type"]>(["currency", "number", "percent"]);

// Shared editor used by both the board table cells and the task detail panel, so a field
// edits identically wherever it appears. `value` is the draft (string | string[]); `onCommit`
// persists it.
export function WorkObjectFieldValueEditor({
  currentWorkObjectId,
  disabled = false,
  field,
  listView,
  onCommit,
  referenceLabels,
  surface,
  value,
}: {
  currentWorkObjectId?: string;
  disabled?: boolean;
  field: WorkObjectFieldDefinition;
  listView?: WorkObjectListView;
  onCommit: (value: WorkObjectFieldValueDraft) => void;
  // Labels for referenced ids that the pickable list does not cover — a
  // related item outside the loaded window, or a member who has since left.
  // Without them such a value would silently render as empty.
  referenceLabels?: Record<string, string>;
  surface: FieldEditorSurface;
  value: WorkObjectFieldValueDraft;
}) {
  const t = useT();
  const asString = typeof value === "string" ? value : "";
  const asArray = Array.isArray(value) ? value : value ? [value] : [];

  if (field.type === "select") {
    const options: ComboOption[] = field.options.map((option) => ({ color: option.color, id: option.id, label: option.value }));
    return <Combobox ariaLabel={field.name} disabled={disabled} onChange={onCommit} options={options} placeholder={emptyValueLabel} surface={surface} value={asString} />;
  }

  if (field.type === "multi_select") {
    const options: ComboOption[] = field.options.map((option) => ({ color: option.color, id: option.id, label: option.value }));
    return <Combobox ariaLabel={field.name} disabled={disabled} multiple onChange={onCommit} options={options} placeholder={emptyValueLabel} surface={surface} value={asArray} />;
  }

  if (isRelationField(field.type)) {
    const pickable = listView ? relationOptions(field, listView, t, currentWorkObjectId) : [];
    const pickableIds = new Set(pickable.map((option) => option.id));
    const selectedIds = Array.isArray(value) ? value : value ? [value] : [];
    const options = [
      ...pickable,
      ...selectedIds
        .filter((id) => !pickableIds.has(id) && referenceLabels?.[id])
        .map((id) => ({ id, label: referenceLabels?.[id] ?? id })),
    ];
    const multiple = isMultiValueField(field.type);
    return (
      <Combobox
        ariaLabel={field.name}
        disabled={disabled}
        multiple={multiple}
        onChange={onCommit}
        options={options}
        placeholder={emptyValueLabel}
        surface={surface}
        value={multiple ? asArray : asString}
      />
    );
  }

  if (field.type === "checkbox") {
    return (
      <label className={cn("flex items-center gap-2", surface === "cell" ? "h-6" : surface === "form" ? "h-7" : "h-7 px-2")}>
        <Checkbox aria-label={field.name} checked={asString === "true"} disabled={disabled} onCheckedChange={(checked) => onCommit(checked === true ? "true" : "false")} onPointerDown={(event) => event.stopPropagation()} />
        {surface !== "cell" ? <span className="text-sm text-muted-foreground">{asString === "true" ? t("common.checked") : t("common.unchecked")}</span> : null}
      </label>
    );
  }

  if (field.type === "rating") {
    return <RatingEditor disabled={disabled} field={field} onCommit={onCommit} surface={surface} value={asString} />;
  }

  if (field.type === "duration") {
    return <DurationEditor ariaLabel={field.name} disabled={disabled} onCommit={onCommit} surface={surface} value={asString} />;
  }

  if (field.type === "date") {
    return (
      <DatePicker
        ariaLabel={field.name}
        className={dateTriggerClass(surface)}
        disabled={disabled}
        onChange={(next) => onCommit(next ? toYmd(next) : "")}
        value={fromYmd(asString)}
      />
    );
  }

  if (field.type === "date_range") {
    const from = fromYmd(asArray[0] ?? "");
    const to = fromYmd(asArray[1] ?? "");
    return (
      <DatePicker
        ariaLabel={field.name}
        className={dateTriggerClass(surface)}
        disabled={disabled}
        mode="range"
        onChange={(range) => {
          if (!range?.from && !range?.to) onCommit([]);
          else if (range?.from && range?.to) onCommit([toYmd(range.from), toYmd(range.to)]);
        }}
        value={from ? { from, to } : undefined}
      />
    );
  }

  if (field.type === "textarea") {
    return <TextareaEditor ariaLabel={field.name} disabled={disabled} onCommit={onCommit} surface={surface} value={asString} />;
  }

  if (field.type === "formula" || field.type === "rollup") {
    const progress = readProgressConfig(field);
    if (progress) {
      return <FieldProgressValue max={progress.max} surface={surface} value={asString} />;
    }
    return <span className={cn("block min-w-0 truncate text-sm", surface === "cell" ? "px-1.5 text-muted-foreground/80" : "px-2 py-1 text-foreground")}>{asString || "—"}</span>;
  }

  if (NUMERIC_TYPES.has(field.type)) {
    const progress = readProgressConfig(field);
    return (
      <div className="min-w-0">
        <NumberFieldEditor
          ariaLabel={field.name}
          controls
          disabled={disabled}
          onCommit={onCommit}
          prefix={field.type === "currency" ? "$" : undefined}
          range={readNumberRange(field)}
          suffix={field.type === "percent" ? "%" : undefined}
          surface={surface}
          value={asString}
        />
        {progress ? <FieldProgressTrack className={surface === "cell" ? "mx-1.5 mb-1" : surface === "form" ? "mx-0.5 mt-1.5" : "mx-2 mb-1"} max={progress.max} value={asString} /> : null}
      </div>
    );
  }

  // text / url / email / phone
  return <TextInputEditor ariaLabel={field.name} disabled={disabled} onCommit={onCommit} surface={surface} type={htmlInputType(field.type)} value={asString} />;
}

// ---------------------------------------------------------------------------
// Text-like inputs (commit on blur / Enter)
// ---------------------------------------------------------------------------

function inputClass(surface: FieldEditorSurface) {
  // On the form surface the design-kit Input renders untouched — that IS the form look.
  if (surface === "form") return "";
  return cn(
    "rounded-md border-transparent bg-transparent shadow-none hover:bg-muted/60 focus-visible:border-input focus-visible:bg-background",
    surface === "cell" ? "h-6 px-1.5 text-sm text-muted-foreground/90" : "h-7 px-2",
  );
}

function TextInputEditor({
  ariaLabel,
  disabled,
  inputMode,
  onCommit,
  prefix,
  suffix,
  surface,
  type,
  value,
}: {
  ariaLabel?: string;
  disabled?: boolean;
  inputMode?: "decimal";
  onCommit: (value: string) => void;
  prefix?: string;
  suffix?: string;
  surface: FieldEditorSurface;
  type: string;
  value: string;
}) {
  const [draft, setDraft] = useState(value);
  const dirty = useRef(false);
  if (!dirty.current && draft !== value) setDraft(value);

  const commit = () => {
    dirty.current = false;
    if (draft !== value) onCommit(draft);
  };

  const input = (
    <Input
      aria-label={ariaLabel}
      className={cn(inputClass(surface), prefix && "pl-5", suffix && "pr-6")}
      disabled={disabled}
      inputMode={inputMode}
      onBlur={commit}
      onChange={(event) => {
        dirty.current = true;
        setDraft(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
        if (event.key === "Escape") {
          dirty.current = false;
          setDraft(value);
          event.currentTarget.blur();
        }
      }}
      onPointerDown={(event) => event.stopPropagation()}
      placeholder={emptyValueLabel}
      type={type}
      value={draft}
    />
  );

  if (!prefix && !suffix) return input;
  return (
    <div className="relative">
      {prefix ? <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{prefix}</span> : null}
      {input}
      {suffix ? <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{suffix}</span> : null}
    </div>
  );
}

// Number / currency / percent — design-kit stepper input. Keeps a numeric draft and commits the
// stringified value on blur (matching the text editors), so the shared value model stays strings.
function NumberFieldEditor({
  ariaLabel,
  controls,
  disabled,
  onCommit,
  prefix,
  range,
  suffix,
  surface,
  value,
}: {
  ariaLabel?: string;
  controls?: boolean;
  disabled?: boolean;
  onCommit: (value: string) => void;
  prefix?: string;
  range?: NumberRange | null;
  suffix?: string;
  surface: FieldEditorSurface;
  value: string;
}) {
  const [draft, setDraft] = useState<number | null>(() => parseNumber(value));
  const dirty = useRef(false);
  const lastValue = useRef(value);
  if (value !== lastValue.current) {
    lastValue.current = value;
    if (!dirty.current) setDraft(parseNumber(value));
  }

  const commit = () => {
    dirty.current = false;
    const next = draft == null ? "" : String(draft);
    if (next !== value) onCommit(next);
  };

  return (
    <NumberInput
      ariaLabel={ariaLabel}
      className={cn(
        surface === "cell" ? "h-6" : "h-7",
        surface === "form" && "border-input bg-input/20 px-2 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30 dark:bg-input/30",
      )}
      controls={controls}
      disabled={disabled}
      max={range?.max}
      min={range?.min}
      onCommit={commit}
      onValueChange={(next) => {
        dirty.current = true;
        setDraft(next);
      }}
      prefix={prefix}
      step={range?.step ?? 1}
      suffix={suffix}
      value={draft}
    />
  );
}

function parseNumber(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toYmd(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function fromYmd(value: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function dateTriggerClass(surface: FieldEditorSurface) {
  if (surface === "cell") return "h-6 px-1.5 text-muted-foreground/90";
  return cn("min-h-7 px-2 py-1", surface === "form" && formTriggerClass);
}

function TextareaEditor({ ariaLabel, disabled, onCommit, surface, value }: { ariaLabel?: string; disabled?: boolean; onCommit: (value: string) => void; surface: FieldEditorSurface; value: string }) {
  const t = useT();
  const [draft, setDraft] = useState(value);
  const [open, setOpen] = useState(false);
  const dirty = useRef(false);
  if (!dirty.current && draft !== value) setDraft(value);

  const commit = () => {
    dirty.current = false;
    if (draft !== value) onCommit(draft);
  };

  if (surface !== "cell") {
    return (
      <Textarea
        aria-label={ariaLabel}
        className="min-h-16 resize-y"
        disabled={disabled}
        onBlur={commit}
        onChange={(event) => {
          dirty.current = true;
          setDraft(event.target.value);
        }}
        placeholder={emptyValueLabel}
        value={draft}
      />
    );
  }

  return (
    <Popover onOpenChange={(next) => { if (!next) commit(); setOpen(next); }} open={open}>
      <PopoverTrigger
        aria-label={ariaLabel}
        className="block h-6 w-full min-w-0 cursor-text truncate rounded-md px-1.5 text-left text-sm text-muted-foreground/90 transition-colors hover:bg-muted/60 hover:text-foreground"
        disabled={disabled}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {value || emptyValueLabel}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 gap-2 p-2" side="bottom">
        <Textarea
          aria-label={ariaLabel}
          className="min-h-24 resize-y text-sm"
          ref={focusWithoutScroll}
          onChange={(event) => {
            dirty.current = true;
            setDraft(event.target.value);
          }}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === "Enter") setOpen(false);
          }}
          value={draft}
        />
        <div className="flex justify-end">
          <Button onClick={() => setOpen(false)} size="sm" type="button">{t("common.done")}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Rating (stars)
// ---------------------------------------------------------------------------

function RatingEditor({ disabled, field, onCommit, surface, value }: { disabled?: boolean; field: WorkObjectFieldDefinition; onCommit: (value: string) => void; surface: FieldEditorSurface; value: string }) {
  const t = useT();
  const scale = readRatingScale(field.config);
  const stars = scale === 5 ? 5 : scale === 10 ? 10 : 5;
  const current = clampRating(Number(value) || 0, stars);
  const [hover, setHover] = useState(0);
  const active = hover || current;
  return (
    <div
      aria-label={field.name}
      className={cn("flex items-center gap-0.5", surface === "cell" ? "px-1" : surface === "form" ? "h-7 px-0.5" : "px-2")}
      onMouseLeave={() => setHover(0)}
      role="group"
    >
      {Array.from({ length: stars }, (_, index) => index + 1).map((position) => (
        <button
          aria-label={t("a11y.rate", { position })}
          className="text-muted-foreground/40 transition-colors hover:text-amber-400 disabled:cursor-not-allowed data-[on=true]:text-amber-400"
          data-on={position <= active}
          disabled={disabled}
          key={position}
          onClick={() => onCommit(position === current ? "" : String(position))}
          onMouseEnter={() => setHover(position)}
          onPointerDown={(event) => event.stopPropagation()}
          type="button"
        >
          <Star className={cn("size-4", position <= active && "fill-current")} />
        </button>
      ))}
    </div>
  );
}

function readRatingScale(config: WorkObjectFieldDefinition["config"] | null | undefined) {
  const scale = typeof config?.ratingScale === "number" ? config.ratingScale : 5;
  return scale === 10 || scale === 100 ? scale : 5;
}

function clampRating(value: number, scale: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(scale, Math.max(0, value));
}

// ---------------------------------------------------------------------------
// Duration (human input → seconds)
// ---------------------------------------------------------------------------

function DurationEditor({ ariaLabel, disabled, onCommit, surface, value }: { ariaLabel?: string; disabled?: boolean; onCommit: (value: string) => void; surface: FieldEditorSurface; value: string }) {
  const t = useT();
  const [draft, setDraft] = useState(formatDurationFromSeconds(value));
  const dirty = useRef(false);
  if (!dirty.current) {
    const formatted = formatDurationFromSeconds(value);
    if (draft !== formatted) setDraft(formatted);
  }
  const commit = () => {
    dirty.current = false;
    const seconds = parseDurationToSeconds(draft);
    const next = seconds === null ? "" : String(seconds);
    if (next !== value) onCommit(next);
    setDraft(formatDurationFromSeconds(next));
  };
  return (
    <Input
      aria-label={ariaLabel}
      className={inputClass(surface)}
      disabled={disabled}
      onBlur={commit}
      onChange={(event) => {
        dirty.current = true;
        setDraft(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
      onPointerDown={(event) => event.stopPropagation()}
      placeholder={t("board.durationExample")}
      value={draft}
    />
  );
}

// ---------------------------------------------------------------------------
// Combobox (Popover-based, single & multiple, searchable)
// ---------------------------------------------------------------------------

// `autoFocus` inside a popover would scroll the page: focus lands while the
// floating popup is still unpositioned at the page's top-left corner, so the
// browser scrolls it into view (visible on the standalone form page, where the
// window is the scroll container). Module-level so the ref stays stable.
const focusWithoutScroll = (element: HTMLInputElement | HTMLTextAreaElement | null) => element?.focus({ preventScroll: true });

function Combobox({
  ariaLabel,
  disabled,
  multiple = false,
  onChange,
  options,
  placeholder = emptyValueLabel,
  searchPlaceholder,
  surface,
  value,
}: {
  ariaLabel?: string;
  disabled?: boolean;
  multiple?: boolean;
  onChange: (value: string | string[]) => void;
  options: ComboOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  surface: FieldEditorSurface;
  value: string | string[];
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const optionsById = useMemo(() => new Map(options.map((option) => [option.id, option])), [options]);
  const selectedIds = Array.isArray(value) ? value : value ? [value] : [];
  const selected = selectedIds.map((id) => optionsById.get(id)).filter(Boolean) as ComboOption[];

  const triggerClass = cn(
    "flex w-full min-w-0 items-center gap-1.5 rounded-md text-left text-sm transition-colors outline-none hover:bg-muted/60 focus-visible:border-input focus-visible:bg-background disabled:cursor-not-allowed disabled:opacity-50",
    surface === "cell" ? "h-6 px-1.5 text-muted-foreground/90" : "min-h-7 px-2 py-1",
    surface === "form" && formTriggerClass,
  );

  // aria-label replaces the trigger's text content as its accessible name, so
  // fold the current selection into it — a screen reader should hear both the
  // field and its value.
  const triggerAriaLabel = ariaLabel
    ? selected.length > 0
      ? `${ariaLabel}: ${selected.map((option) => option.label).join(", ")}`
      : ariaLabel
    : undefined;

  // Trigger + popup internals are shared between the single and multiple Roots
  // below (the generic Root types value as `Option` vs `Option[]`).
  const content = (
    <>
      <ComboboxPrimitive.Trigger aria-label={triggerAriaLabel} className={triggerClass} disabled={disabled} onPointerDown={(event) => event.stopPropagation()}>
        {selected.length > 0 ? (
          multiple ? (
            <span className="flex min-w-0 flex-wrap items-center gap-1">
              {selected.map((option) => <OptionChip key={option.id} option={option} />)}
            </span>
          ) : (
            <OptionLabel option={selected[0]!} />
          )
        ) : (
          <span className="min-w-0 truncate text-muted-foreground/70">{placeholder}</span>
        )}
        <ChevronDown className="ml-auto size-3.5 shrink-0 text-muted-foreground/60" />
      </ComboboxPrimitive.Trigger>
      <ComboboxPrimitive.Portal>
        <ComboboxPrimitive.Positioner align="start" className="isolate z-50" side="bottom" sideOffset={4}>
          <ComboboxPrimitive.Popup className="z-50 flex w-64 origin-(--transform-origin) flex-col overflow-hidden rounded-lg bg-popover text-sm text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-hidden duration-100 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95">
            <div className="flex items-center gap-2 border-b border-border/60 px-2.5">
              <Search className="size-3.5 shrink-0 text-muted-foreground" />
              <ComboboxPrimitive.Input
                aria-label={searchPlaceholder ?? t("common.search")}
                className="h-8 w-full min-w-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
                placeholder={searchPlaceholder ?? t("common.search")}
              />
            </div>
            <ComboboxPrimitive.Empty className="not-empty:px-2 not-empty:py-3 text-center text-xs text-muted-foreground">
              {t("common.noMatches")}
            </ComboboxPrimitive.Empty>
            <ComboboxPrimitive.List className="max-h-64 overflow-y-auto p-1 empty:p-0">
              {(option: ComboOption) => (
                <ComboboxPrimitive.Item
                  className="flex w-full cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm outline-none transition-colors data-highlighted:bg-muted/60"
                  key={option.id}
                  value={option}
                >
                  {option.icon ?? (option.color ? <span className="size-2.5 shrink-0 rounded-sm" style={{ backgroundColor: option.color }} /> : null)}
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {option.note ? <span className="shrink-0 text-xs text-muted-foreground">{option.note}</span> : null}
                  <ComboboxPrimitive.ItemIndicator>
                    <Check className="size-3.5 shrink-0 text-primary" />
                  </ComboboxPrimitive.ItemIndicator>
                </ComboboxPrimitive.Item>
              )}
            </ComboboxPrimitive.List>
            {!multiple && selectedIds.length > 0 ? (
              <div className="border-t border-border/60 p-1">
                <button className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors outline-none hover:bg-muted/60 focus-visible:bg-muted/60" onClick={() => { onChange(""); setOpen(false); }} type="button">
                  <X className="size-3.5" /> {t("common.clear")}
                </button>
              </div>
            ) : null}
          </ComboboxPrimitive.Popup>
        </ComboboxPrimitive.Positioner>
      </ComboboxPrimitive.Portal>
    </>
  );

  const shared = {
    disabled,
    isItemEqualToValue: (a: ComboOption, b: ComboOption) => a.id === b.id,
    items: options,
    itemToStringLabel: (option: ComboOption) => option.label,
    onOpenChange: setOpen,
    open,
  };

  if (multiple) {
    return (
      <ComboboxPrimitive.Root
        {...shared}
        multiple
        onValueChange={(next) => onChange(next.map((option) => option.id))}
        value={selected}
      >
        {content}
      </ComboboxPrimitive.Root>
    );
  }
  return (
    <ComboboxPrimitive.Root
      {...shared}
      onValueChange={(next) => onChange(next?.id ?? "")}
      value={selected[0] ?? null}
    >
      {content}
    </ComboboxPrimitive.Root>
  );
}

function OptionLabel({ option }: { option: ComboOption }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5 truncate">
      {option.icon ?? (option.color ? <span className="size-2.5 shrink-0 rounded-sm" style={{ backgroundColor: option.color }} /> : null)}
      <span className="min-w-0 truncate text-foreground">{option.label}</span>
    </span>
  );
}

function OptionChip({ option }: { option: ComboOption }) {
  return (
    <span className="flex max-w-full items-center gap-1 rounded-sm bg-muted px-1.5 py-0.5 text-xs">
      {option.icon ?? (option.color ? <span className="size-2 shrink-0 rounded-sm" style={{ backgroundColor: option.color }} /> : null)}
      <span className="min-w-0 truncate">{option.label}</span>
    </span>
  );
}

function relationOptions(
  field: WorkObjectFieldDefinition,
  listView: WorkObjectListView,
  t: Translate,
  currentWorkObjectId?: string,
): ComboOption[] {
  if (field.type === "user_relation" || field.type === "user_multi_relation") {
    // Роль поля. «QA» с командой тестировщиков показывает их первыми, остальных
    // — ниже: раньше в списке был весь состав области, и на поле для одной роли
    // предлагались все подряд. Запрет намеренно мягкий — подменить коллегу
    // бывает нужно, и жёсткое правило ломало бы такие случаи.
    const teamId = (field.config as { personScope?: { teamId?: string } } | undefined)?.personScope?.teamId;
    const inTeam = (user: WorkObjectListView["users"][number]) => Boolean(teamId && user.teamIds?.includes(teamId));
    const ordered = teamId
      ? [...listView.users].sort((left, right) => Number(inTeam(right)) - Number(inTeam(left)))
      : listView.users;
    return ordered.map((user) => {
      const label = user.displayName ?? user.email;
      // Назначать можно и на того, кто ещё не заходил, но полезно видеть, что
      // человек про задачу пока не узнает.
      const note = !user.activatedAt ? t("team.notSignedIn") : teamId && !inTeam(user) ? t("team.outsideRole") : undefined;
      return {
        icon: <UserAvatar label={label} />,
        id: user.id,
        label,
        note,
      };
    });
  }
  // Кандидаты на связь. Раньше сюда попадала КАЖДАЯ карточка области, одним
  // только названием: на живом трекере это сотни строк, среди которых десяток
  // «Задача 2.1 — Реализовать callba…» неразличимы. Теперь список сужен до
  // допустимых типов цели, у каждой строки виден ключ и тип, а свежие идут
  // первыми — по ним и попадают чаще всего.
  const allowedTypeIds = new Set(field.relationTargetTypeIds);
  // Похожие по названию — наверх. Связывают обычно с тем, о чём карточка и
  // написана: баг «Не открывается экспорт» с историей про экспорт. Раньше это
  // приходилось искать глазами среди сотен строк, отсортированных никак.
  const currentTitle = listView.workObjects.find((item) => item.id === currentWorkObjectId)?.title ?? "";
  const currentWords = titleWords(currentTitle);
  const scored = listView.workObjects
    .filter((item) => item.id !== currentWorkObjectId)
    .filter((item) => allowedTypeIds.size === 0 || (item.type?.id ? allowedTypeIds.has(item.type.id) : false))
    .map((item) => {
      const shared = currentWords.size === 0 ? 0 : [...titleWords(item.title)].filter((word) => currentWords.has(word)).length;
      return { item, shared };
    });
  scored.sort((left, right) => right.shared - left.shared || new Date(right.item.createdAt).getTime() - new Date(left.item.createdAt).getTime());
  return scored.map(({ item, shared }) => ({
    icon: item.itemKey ? <ItemKeyBadge itemKey={item.itemKey} /> : undefined,
    id: item.id,
    label: item.title,
    // Приписка говорит, ПОЧЕМУ строка наверху: иначе порядок выглядит
    // случайным, и человек всё равно листает весь список.
    note: shared > 0 ? t("board.similarByTitle") : item.type?.name ?? undefined,
  }));
}

// Значимые слова названия. Короткие отбрасываются: «на», «и», «the» совпадают
// у всего подряд и сделали бы похожим что угодно.
function titleWords(title: string): Set<string> {
  return new Set(
    title
      .toLowerCase()
      .split(/[^\p{L}\p{N}]+/u)
      .filter((word) => word.length > 3),
  );
}

// Ключ карточки рядом с названием: по нему её называют вслух и ищут глазами,
// и он же отличает две одинаково начинающиеся строки друг от друга.
function ItemKeyBadge({ itemKey }: { itemKey: string }) {
  return <span className="shrink-0 font-mono text-[10px] text-muted-foreground/70">{itemKey}</span>;
}

function UserAvatar({ label }: { label: string }) {
  const initials = label.split(/\s+/).map((part) => part[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
  return <span className="grid size-4 shrink-0 place-items-center rounded-full bg-primary/15 text-[0.5rem] font-semibold text-primary">{initials}</span>;
}

function htmlInputType(type: WorkObjectFieldDefinition["type"]) {
  if (type === "number" || type === "currency" || type === "percent") return "number";
  if (type === "url") return "url";
  if (type === "email") return "email";
  if (type === "phone") return "tel";
  return "text";
}

// ---------------------------------------------------------------------------
// Progress display for numeric fields configured with { progress: { max } }
// ---------------------------------------------------------------------------

export type NumberRange = { max?: number; min?: number; step?: number };

// Границы числового поля из его настройки. Тот же объект, что проверяет домен,
// поэтому ввод не даёт набрать то, что сервер потом отвергнет.
export function readNumberRange(field: Pick<WorkObjectFieldDefinition, "config" | "type">): NumberRange | null {
  const raw = (field.config as { numberRange?: { max?: unknown; min?: unknown; step?: unknown } } | undefined)?.numberRange;
  if (!raw || typeof raw !== "object") return null;
  const pick = (value: unknown) => (Number.isFinite(Number(value)) && value !== null && value !== "" ? Number(value) : undefined);
  const range = { max: pick(raw.max), min: pick(raw.min), step: pick(raw.step) };
  return range.max === undefined && range.min === undefined && range.step === undefined ? null : range;
}

export function readProgressConfig(field: Pick<WorkObjectFieldDefinition, "config" | "type">): { max: number } | null {
  if (!["formula", "number", "percent", "rollup"].includes(field.type)) return null;
  const raw = (field.config as { progress?: { max?: unknown } } | undefined)?.progress;
  if (!raw || typeof raw !== "object") return null;
  const max = Number(raw.max);
  return Number.isFinite(max) && max > 0 ? { max } : { max: 100 };
}

export function FieldProgressTrack({ className, max, value }: { className?: string; max: number; value: string }) {
  const numeric = Number(value);
  const ratio = Number.isFinite(numeric) ? Math.min(Math.max(numeric / max, 0), 1) : 0;
  return (
    <div
      aria-valuemax={max}
      aria-valuemin={0}
      aria-valuenow={Number.isFinite(numeric) ? numeric : 0}
      className={cn("h-1 overflow-hidden rounded-full bg-muted", className)}
      role="progressbar"
    >
      <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${ratio * 100}%` }} />
    </div>
  );
}

// Read-only bar + value, used for computed fields (formula / rollup).
export function FieldProgressValue({ max, surface, value }: { max: number; surface: FieldEditorSurface; value: string }) {
  const numeric = Number(value);
  const hasValue = value !== "" && Number.isFinite(numeric);
  return (
    <div className={cn("flex min-w-0 items-center gap-2", surface === "cell" ? "px-1.5" : "px-2 py-1")}>
      <FieldProgressTrack className="min-w-10 flex-1" max={max} value={value} />
      <span className={cn("shrink-0 tabular-nums text-xs", surface === "cell" ? "text-muted-foreground/80" : "text-foreground")}>
        {hasValue ? `${Math.round((Math.min(Math.max(numeric / max, 0), 1)) * 100)}%` : "—"}
      </span>
    </div>
  );
}
